import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import AiModeButton from './AiModeButton';
import AiChatPanel from './AiChatPanel';
import { fetchFeaturesPermission } from '../../utils/utility';
import { AI_CHATBOT } from '../../constants/permissionConstants';

/**
 * AI Mode entry point: purple io Genie FAB + floating / fullscreen
 * chat panel (Zeplin ioGenie AI Assistant).
 *
 * Visibility:
 *   - Closed (X) → round FAB only. Conversation is archived/reset.
 *   - Minimized → compact active bar (header chrome). Click-outside,
 *     the dash control, or an offer-accept all land here so Canvas
 *     stays usable without losing chat context. Click the bar to restore.
 *   - Expanded → floating window, or a larger floating fullscreen window.
 *
 * @return {JSX.Element} The AI chat module with FAB trigger and optional panel portal.
 */
export default function AiChatModule() {
	// `hasOpened` flips to true the first time the user clicks the
	// floating icon, and stays true for the rest of the page lifetime.
	// We use it to keep the chat panel MOUNTED (and useHfclChat alive)
	// even when the panel is visually hidden, so the conversation
	// history survives every minimize / re-open cycle. A real page
	// refresh remounts the whole tree and clears state, exactly as
	// the user requested.
	const [hasOpened, setHasOpened] = useState(false);
	// `isHidden` = fully closed to the FAB (X). The active bar is NOT hidden.
	const [isHidden, setIsHidden] = useState(true);
	// Zeplin active bar: header-only strip, conversation kept mounted.
	const [isMinimized, setIsMinimized] = useState(false);

	const tourStep = useSelector((state) => state.tourGuideStep.tourStep);
	const isTourBlocking = tourStep === 2 || tourStep === 3;
	const isAiChatbotEnabled = fetchFeaturesPermission(AI_CHATBOT);

	// Track WHY the panel left the expanded window.
	//   'action' → offer accept: stay on the active bar, do not auto-expand
	//              a new reply over the form the user just opened.
	//   'user'   → X close to FAB: a new assistant reply should reopen.
	const lastHideReasonRef = useRef('user');

	const handleToggle = () => {
		if (isTourBlocking) return;
		if (!hasOpened) {
			setHasOpened(true);
			setIsHidden(false);
			setIsMinimized(false);
			return;
		}
		if (isHidden) {
			setIsHidden(false);
			setIsMinimized(false);
			return;
		}
		if (isMinimized) {
			setIsMinimized(false);
			return;
		}
		setIsMinimized(true);
	};

	const handleUserHide = () => {
		lastHideReasonRef.current = 'user';
		setIsHidden(true);
		setIsMinimized(false);
	};
	const handleMinimize = () => {
		setIsMinimized(true);
		setIsHidden(false);
	};
	const handleRestore = () => {
		setIsMinimized(false);
		setIsHidden(false);
	};
	const handleActionHide = () => {
		lastHideReasonRef.current = 'action';
		setIsMinimized(true);
		setIsHidden(false);
	};

	const handleNewReplyWhileHidden = () => {
		if (lastHideReasonRef.current === 'action') return;
		setIsHidden(false);
		setIsMinimized(false);
	};
	const canUsePortal = typeof document !== 'undefined';

	if (!isAiChatbotEnabled) {
		return null;
	}

	return (
		<>
			{canUsePortal
				? createPortal(
						<AiModeButton
							onClick={handleToggle}
							isActive={hasOpened && !isHidden}
							disabled={isTourBlocking}
						/>,
						document.body
				  )
				: (
					<AiModeButton
						onClick={handleToggle}
						isActive={hasOpened && !isHidden}
						disabled={isTourBlocking}
					/>
				)}
			{hasOpened && canUsePortal &&
				createPortal(
					<AiChatPanel
						onClose={handleUserHide}
						isMinimized={isMinimized}
						onMinimize={handleMinimize}
						onRestore={handleRestore}
						hidden={isHidden}
						onAction={handleActionHide}
						onNewReplyWhileHidden={handleNewReplyWhileHidden}
					/>,
					document.body
				)}
		</>
	);
}
